<template>
  <div class="card table-card" style="overflow: visible !important;">
    <div class="card-body" style="overflow: visible !important;">
      <!-- Barra di ricerca e filtri -->
      <div class="mb-4 d-flex gap-3 align-items-center">
        <div class="search-container flex-grow-1" v-if="showSearch">
          <div class="input-group">
            <span class="input-group-text"><i class="bi bi-search"></i></span>
            <input
              type="text"
              class="form-control"
              :placeholder="searchPlaceholder"
              :value="searchQuery"
              @input="handleSearch($event)"
            >
          </div>
        </div>
        <!-- Slot per filtri aggiuntivi -->
        <slot name="filters" class="flex-shrink-1"></slot>
      </div>

      <div class="table-responsive" style="overflow: visible !important;">
        <table class="table table-hover modern-table">
          <thead>
            <tr>
              <th v-for="column in columns" :key="column.key" 
                  :class="{ 'sortable': column.sortable, 'sorted': sortField === (column.sortableColumn || column.key) }" 
                  @click="column.sortable && handleSort(column.key, column.sortableColumn)">
                  <span v-if="column.sortable" class="sort-icon ms-1">
                    {{ column.label }}
                    <i v-if="sortField === (column.sortableColumn || column.key)" 
                       :class="sortDirection === 'asc' ? 'bi bi-caret-up-fill' : 'bi bi-caret-down-fill'"></i>
                    <i v-else class="bi bi-filter"></i>
                  </span>
                  <span v-else>{{ column.label }}</span>
              </th>
              <th v-if="hasActions" class="actions-column"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in items" :key="item.id" class="table-row-hover">
              <td v-for="column in columns" :key="column.key">
                <!-- Slot dinamico per ogni colonna - supporta sia chiavi normali che dot notation -->
                <slot :name="column.key" :item="item">
                  <!-- Prova con lo slot normalizzato (sostituisce i punti con trattini bassi) -->
                  <slot :name="column.key.replace(/\./g, '_')" :item="item">
                    {{ getNestedValue(item, column.key) }}
                  </slot>
                </slot>
              </td>
              <!-- Slot per le azioni -->
              <td v-if="hasActions" class="actions-column">
                <slot name="actions" :item="item"></slot>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Paginazione -->
        <div class="d-flex justify-content-between align-items-center mt-3" v-if="pagination">
          <div class="text-muted">
            Mostrando {{ (pagination.currentPage - 1) * pagination.perPage + 1 }} - 
            {{ Math.min(pagination.currentPage * pagination.perPage, pagination.total) }} 
            di {{ pagination.total }} elementi
          </div>
          <nav aria-label="Page navigation">
            <ul class="pagination mb-0">
              <!-- Pulsante pagina precedente -->
              <li class="page-item" :class="{ disabled: pagination.currentPage === 1 }">
                <a class="page-link" href="#" @click.prevent="$emit('page-change', pagination.currentPage - 1)">
                  <i class="bi bi-chevron-left"></i>
                </a>
              </li>
              
              <!-- Prima pagina -->
              <li v-if="pagination.currentPage > 3" class="page-item">
                <a class="page-link" href="#" @click.prevent="$emit('page-change', 1)">1</a>
              </li>
              
              <!-- Ellipsis se necessario -->
              <li v-if="pagination.currentPage > 4" class="page-item disabled">
                <span class="page-link">...</span>
              </li>
              
              <!-- Pagine attorno alla pagina corrente -->
              <li v-for="page in visiblePageNumbers" :key="page" class="page-item" :class="{ active: page === pagination.currentPage }">
                <a class="page-link" href="#" @click.prevent="$emit('page-change', page)">{{ page }}</a>
              </li>
              
              <!-- Ellipsis se necessario -->
              <li v-if="pagination.currentPage < pagination.lastPage - 3" class="page-item disabled">
                <span class="page-link">...</span>
              </li>
              
              <!-- Ultima pagina -->
              <li v-if="pagination.currentPage < pagination.lastPage - 2" class="page-item">
                <a class="page-link" href="#" @click.prevent="$emit('page-change', pagination.lastPage)">{{ pagination.lastPage }}</a>
              </li>
              
              <!-- Pulsante pagina successiva -->
              <li class="page-item" :class="{ disabled: pagination.currentPage === pagination.lastPage }">
                <a class="page-link" href="#" @click.prevent="$emit('page-change', pagination.currentPage + 1)">
                  <i class="bi bi-chevron-right"></i>
                </a>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TableView',
  props: {
    searchQuery: {
      type: String,
      default: ''
    },
    // Configurazione colonne: [{ key: 'name', label: 'Nome', sortable: true }, ...]
    columns: {
      type: Array,
      required: true
    },
    // Dati da visualizzare
    items: {
      type: Array,
      required: true
    },
    // Configurazione paginazione
    pagination: {
      type: Object,
      default: null
    },
    // Mostra la barra di ricerca
    showSearch: {
      type: Boolean,
      default: true
    },
    // Placeholder per la barra di ricerca
    searchPlaceholder: {
      type: String,
      default: 'Cerca...'
    },
    // Ha azioni (menu a tre punti)
    hasActions: {
      type: Boolean,
      default: true
    },
    // Campo di ordinamento attuale
    sortField: {
      type: String,
      default: ''
    },
    // Direzione di ordinamento (asc o desc)
    sortDirection: {
      type: String,
      default: 'asc',
      validator: (value) => ['asc', 'desc'].includes(value)
    }
  },

  emits: ['search', 'page-change', 'update:searchQuery', 'sort'],
  
  computed: {
    visiblePageNumbers() {
      if (!this.pagination) return [];
      
      const currentPage = this.pagination.currentPage;
      const lastPage = this.pagination.lastPage;
      const delta = 2; // Numero di pagine da mostrare prima e dopo la pagina corrente
      
      let start = Math.max(1, currentPage - delta);
      let end = Math.min(lastPage, currentPage + delta);
      
      // Assicuriamoci di mostrare sempre 2*delta+1 pagine se possibile
      if (end - start < 2 * delta) {
        if (start === 1) {
          end = Math.min(start + 2 * delta, lastPage);
        } else if (end === lastPage) {
          start = Math.max(end - 2 * delta, 1);
        }
      }
      
      // Genera l'array di numeri di pagina da mostrare
      const pages = [];
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      
      return pages;
    }
  },
  
  methods: {
    handleSearch(event) {
      const value = event.target.value
      this.$emit('update:searchQuery', value)
      this.$emit('search', value)
    },
    
    /**
     * Gestisce il click su una colonna ordinabile
     * @param {String} field - Il campo visualizzato nella tabella (key della colonna)
     * @param {String} sortableColumn - Campo opzionale da utilizzare per l'ordinamento invece di field
     */
    handleSort(field, sortableColumn) {
      // Utilizziamo sortableColumn se presente, altrimenti field
      const sortField = sortableColumn || field;
      let direction = 'asc';
      
      // Se stiamo già ordinando per questo campo, invertiamo la direzione
      if (sortField === this.sortField) {
        direction = this.sortDirection === 'asc' ? 'desc' : 'asc';
      }
      
      // Emettiamo l'evento di ordinamento
      this.$emit('sort', { field: sortField, direction });
    },
    
    /**
     * Ottiene il valore di una proprietà annidata utilizzando la notazione a punti
     * @param {Object} obj - L'oggetto da cui estrarre il valore
     * @param {String} path - Il percorso della proprietà (es. 'content.subject')
     * @returns {*} Il valore della proprietà o undefined se non esiste
     */
    getNestedValue(obj, path) {
      if (!obj || !path) return undefined;
      
      // Gestisce sia la notazione a punti che l'accesso diretto
      if (!path.includes('.')) {
        return obj[path];
      }
      
      // Divide il percorso in parti e naviga nell'oggetto
      return path.split('.').reduce((prev, curr) => {
        return prev && prev[curr] !== undefined ? prev[curr] : undefined;
      }, obj);
    },
    
    /**
     * Normalizza un nome di slot con notazione a punti in un nome valido per gli slot Vue
     * @param {String} key - La chiave della colonna (es. 'content.subject')
     * @returns {String} Il nome dello slot normalizzato (es. 'content_subject')
     */
    normalizeSlotName(key) {
      // Sostituisce i punti con trattini bassi per creare un nome di slot valido
      return key.replace(/\./g, '_');
    }
  }
}
</script>

<style scoped>
.table-card {
  border-radius: 0.75rem;
  border: none;
  box-shadow: 0 0.125rem 0.375rem rgba(var(--bs-body-color-rgb), 0.05);
  overflow: hidden;
}

.search-container .input-group {
  box-shadow: 0 0.125rem 0.25rem rgba(var(--bs-body-color-rgb), 0.03);
  border-radius: 0.5rem;
  overflow: hidden;
}

.search-container .input-group-text {
  background-color: var(--bs-body-bg);
  border-right: none;
  color: var(--bs-body-color);
}

.search-container .form-control {
  border-left: none;
}

.modern-table {
  border-collapse: separate;
  border-spacing: 0;
}

.modern-table thead th {
  background-color: var(--bs-tertiary-bg);
  border-top: none;
  border-bottom: 1px solid var(--bs-border-color);
  font-weight: 600;
  color: var(--bs-body-color);
  padding: 1rem;
  text-transform: uppercase;
  font-size: 0.75rem;
  letter-spacing: 0.5px;
}

.modern-table tbody td {
  padding: 1rem;
  vertical-align: middle;
  border-bottom: 1px solid var(--bs-border-color-translucent);
}

.modern-table tbody tr:last-child td {
  border-bottom: none;
}

.actions-column {
  width: 100px;
  text-align: right;
}

.table-row-hover:hover {
  background-color: rgba(var(--bs-primary-rgb), 0.03);
}

/* Stili per le colonne ordinabili */
th.sortable {
  cursor: pointer;
  user-select: none;
  position: relative;
}

th.sortable:hover {
  background-color: rgba(var(--bs-primary-rgb), 0.05);
}

th.sorted {
  background-color: rgba(var(--bs-primary-rgb), 0.08);
}

.sort-icon {
  display: inline-block;
  opacity: 0.6;
}

th.sorted .sort-icon {
  opacity: 1;
  color: var(--bs-primary);
}

th > span {
  white-space: nowrap;
}

/* Paginazione */
.pagination {
  margin-bottom: 0;
}

.pagination .page-link {
  border-radius: 0.25rem;
  margin: 0 0.125rem;
  color: var(--bs-primary);
  border: 1px solid var(--bs-border-color);
  background-color: var(--bs-body-bg);
}

.pagination .page-item.active .page-link {
  background-color: var(--bs-primary);
  border-color: var(--bs-primary);
  box-shadow: 0 2px 5px rgba(var(--bs-primary-rgb), 0.2);
  color: var(--bs-white);
}

.pagination .page-link:hover {
  background-color: var(--bs-tertiary-bg);
  border-color: var(--bs-border-color);
}

.pagination .page-item.active .page-link:hover {
  background-color: #0d6efd;
}
</style>